import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import useSubmitGuard from '../hooks/useSubmitGuard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Card, CardContent } from '../components/ui/card';
import { 
  Plus, 
  Trash2,
  Wrench,
  Truck,
  Cog,
  AlertCircle,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Timer,
  TriangleAlert,
  Pencil,
  Eye,
  CalendarClock,
  Upload,
  Download,
  Loader2,
  LogIn,
  LogOut,
  Camera,
  X,
  Timer as TimerIcon,
} from 'lucide-react';
import { DialogFooter } from '../components/ui/dialog';
import { format } from 'date-fns';
import { ExportButton } from '../utils/exportUtils';
import { offlineDB } from '../utils/offlineDB';
import DeleteRequestDialog from '../components/DeleteRequestDialog';
import PendingDeletionsPanel from '../components/PendingDeletionsPanel';
import SearchableSelect from '../components/SearchableSelect';
import { 
  FilterBar, 
  DateRangeFilter, 
  SelectFilter, 
  SearchFilter,
  countActiveFilters,
  filterByDateRange 
} from '../components/FilterComponents';

// Safe date formatter — prevents "Invalid time value" crash on malformed production data
const safeFormat = (dateVal, fmt, fallback = '-') => {
  if (!dateVal) return fallback;
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return fallback;
    return format(d, fmt);
  } catch {
    return fallback;
  }
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mirrors backend diagnosis quality check
function checkDiagnosisQuality(diagnosis) {
  const text = diagnosis.trim();
  const words = text.split(/\s+/).filter(w => w.length > 0);
  if (words.length < 2) return 'Diagnosis is too short — please enter at least 2 words.';
  if (/(.)\1{3,}/i.test(text)) return 'Diagnosis contains repeated characters (e.g. "aaaa"). Please describe the fault properly.';
  if (/(\d)\1{3,}/.test(text)) return 'Diagnosis contains repeated numbers (e.g. "1111"). Please describe the fault properly.';
  return null;
}

const MAINTENANCE_TYPES = [
  { id: 'scheduled', label: 'Scheduled Maintenance' },
  { id: 'preventive', label: 'Preventive Maintenance' },
  { id: 'running_repair', label: 'Running Repair' },
  { id: 'running_with_problems', label: 'Running with Problems' },
  { id: 'breakdown', label: 'Breakdown' },
];

const REPAIR_STATUS_CONFIG = {
  running_repair: { 
    label: 'Running Repair', 
    description: '< 48 hours', 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    icon: Timer
  },
  breakdown: { 
    label: 'Breakdown', 
    description: '48 hrs - 7 days', 
    color: 'bg-orange-100 text-orange-800 border-orange-300',
    icon: AlertCircle
  },
  long_term_breakdown: { 
    label: 'Long Term Breakdown', 
    description: '> 7 days', 
    color: 'bg-red-100 text-red-800 border-red-300',
    icon: AlertTriangle
  },
  completed: { 
    label: 'Completed', 
    description: 'Repaired', 
    color: 'bg-green-100 text-green-800 border-green-300',
    icon: CheckCircle2
  },
};

const MaintenancePage = ({ user }) => {
  const { section: urlSection } = useParams();
  const activeSection = urlSection || 'truck';
  const guard = useSubmitGuard();
  
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [repairStatusFilter, setRepairStatusFilter] = useState('all');

  // View / Edit log state
  const [viewingLog, setViewingLog] = useState(null);
  const [isEditLogOpen, setIsEditLogOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  
  // Filters
  const [filters, setFilters] = useState({
    dateRange: { from: null, to: null },
    status: '',
    vehicle: '',
    maintenanceType: ''
  });

  const resetFilters = () => {
    setFilters({
      dateRange: { from: null, to: null },
      status: '',
      vehicle: '',
      maintenanceType: ''
    });
    setRepairStatusFilter('all');
  };
  
  const [isAddLogOpen, setIsAddLogOpen] = useState(false);
  const [isAddOutOpen, setIsAddOutOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // Import Excel
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/maintenance/bulk-import`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
      if (res.data.imported > 0) {
        toast.success(`Imported ${res.data.imported} maintenance records!`);
        fetchData();
      }
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} rows had errors`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/maintenance/import-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'maintenance_import_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  // Delete approval
  const canApprove = user?.is_admin || user?.is_super_admin;
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pendingDelRequests, setPendingDelRequests] = useState([]);

  const fetchPendingDeletions = async () => {
    if (!canApprove) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/delete-requests?status=pending&module=maintenance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingDelRequests(res.data);
    } catch {}
  };

  const handleApproveDelete = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deletion approved');
      fetchData();
      fetchPendingDeletions();
    } catch { toast.error('Failed to approve'); }
  };

  const handleRejectDelete = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/reject`, { note: 'Rejected' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Request rejected');
      fetchPendingDeletions();
    } catch { toast.error('Failed to reject'); }
  };
  
  const [logForm, setLogForm] = useState({
    section: activeSection,
    door_no: '',
    maintenance_type: '',
    reporting_time: '',
    diagnosis: '',
    parts_required: [],
    remarks: '',
    damage_photos: [],
  });

  const [outForm, setOutForm] = useState({
    log_id: '',
    out_time: '',
    close_remarks: '',
  });

  const [partForm, setPartForm] = useState({
    sku_id: '',
    part_name: '',
    quantity: 1,
    required_in_days: '',
  });

  useEffect(() => {
    setLogForm(prev => ({ ...prev, section: activeSection }));
    fetchData();
    fetchPendingDeletions();
    window.addEventListener('offlineSyncComplete', fetchData);
    return () => window.removeEventListener('offlineSyncComplete', fetchData);
  }, [activeSection]);

  // Deep-link from Global Search: /maintenance/{section}?log=<id> opens the log viewer
  useEffect(() => {
    const target = searchParams.get('log');
    if (!target || logs.length === 0) return;
    const log = logs.find(l => l.id === target);
    if (!log) return;
    setViewingLog(log);
    // Clean the param so reload doesn't re-open the dialog
    const next = new URLSearchParams(searchParams);
    next.delete('log');
    setSearchParams(next, { replace: true });
  }, [searchParams, logs, setSearchParams]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [logsRes, statsRes, inventoryRes, assetsRes] = await Promise.all([
        axios.get(`${API}/maintenance/logs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/maintenance/stats?section=${activeSection}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/inventory`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/assets`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
      setInventory(inventoryRes.data);
      setAssets(assetsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLog = async (e) => {
    e.preventDefault();
    guard(async () => {
      if (!navigator.onLine) {
        try {
          await offlineDB.add({
            module: 'maintenance',
            displayName: `Maintenance – ${logForm.door_no || 'Unknown'}`,
            endpoint: '/maintenance/logs',
            method: 'POST',
            data: logForm,
          });
          const count = await offlineDB.pendingCount();
          if (window.__setOfflinePendingCount) window.__setOfflinePendingCount(count);
          toast.success('Saved locally — will sync when back online', {
            description: 'Maintenance log stored on this device',
            duration: 4000,
          });
          setIsAddLogOpen(false);
          resetLogForm();
        } catch (_e) {
          toast.error('Failed to save offline. Storage may be full.');
        }
        return;
      }
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/maintenance/logs`, logForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Maintenance log created');
        fetchData();
        setIsAddLogOpen(false);
        resetLogForm();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create log');
      }
    });
  };

  const handleUpdateStatus = async (logId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/maintenance/logs/${logId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteLog = async (log) => {
    if (canApprove) {
      if (!window.confirm('Delete this maintenance log?')) return;
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/maintenance/logs/${log.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Log deleted');
        fetchData();
        fetchPendingDeletions();
      } catch { toast.error('Failed to delete log'); }
    } else {
      setDeleteTarget({ id: log.id, display: `Maintenance – ${log.door_no} (${log.maintenance_type})` });
    }
  };

  const resetLogForm = () => {
    setLogForm({
      section: activeSection,
      door_no: '',
      maintenance_type: '',
      reporting_time: '',
      diagnosis: '',
      parts_required: [],
      remarks: '',
      damage_photos: [],
    });
    setPartForm({ sku_id: '', part_name: '', quantity: 1, required_in_days: '' });
  };

  const resetOutForm = () => {
    setOutForm({ log_id: '', out_time: '', close_remarks: '' });
  };

  const handleAddOut = async (e) => {
    e.preventDefault();
    if (!outForm.log_id) { toast.error('Please select a ticket'); return; }
    if (!outForm.out_time) { toast.error('Please enter out time'); return; }
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API}/maintenance/logs/${outForm.log_id}/out`,
          { out_time: outForm.out_time, close_remarks: outForm.close_remarks },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Vehicle out time recorded — ticket closed');
        fetchData();
        setIsAddOutOpen(false);
        resetOutForm();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to record out time');
      }
    });
  };

  // Photo upload — compress to max 800px JPEG @ 70% quality
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (logForm.damage_photos.length + files.length > 5) {
      toast.error('Maximum 5 photos allowed');
      return;
    }
    files.forEach(file => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = (ev) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX = 800;
          let w = img.width, h = img.height;
          if (w > MAX || h > MAX) {
            if (w > h) { h = Math.round((h * MAX) / w); w = MAX; }
            else { w = Math.round((w * MAX) / h); h = MAX; }
          }
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          const b64 = canvas.toDataURL('image/jpeg', 0.7);
          setLogForm(prev => ({ ...prev, damage_photos: [...prev.damage_photos, b64] }));
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removePhoto = (idx) => {
    setLogForm(prev => ({ ...prev, damage_photos: prev.damage_photos.filter((_, i) => i !== idx) }));
  };

  const openEditLog = (log) => {
    setEditingLog(log);
    // Format reporting_time for datetime-local input
    const rt = log.reporting_time
      ? (() => { try { const d = new Date(log.reporting_time); return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 16); } catch { return ''; } })()
      : '';
    setLogForm({
      section: log.section || activeSection,
      door_no: log.door_no,
      maintenance_type: log.maintenance_type,
      reporting_time: rt,
      diagnosis: log.diagnosis || '',
      parts_required: log.parts_required || [],
      remarks: log.remarks || '',
      damage_photos: log.damage_photos || [],
    });
    setIsEditLogOpen(true);
  };

  const handleEditLog = async (e) => {
    e.preventDefault();
    if (!editingLog) return;
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API}/maintenance/logs/${editingLog.id}`, logForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Maintenance log updated');
        setIsEditLogOpen(false);
        setEditingLog(null);
        resetLogForm();
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to update log');
      }
    });
  };

  const addPart = () => {
    if (!partForm.sku_id && !partForm.part_name) {
      toast.error('Select a part or enter part name');
      return;
    }
    
    const selectedSKU = inventory.find(s => s.id === partForm.sku_id);
    setLogForm({
      ...logForm,
      parts_required: [...logForm.parts_required, {
        sku_id: partForm.sku_id,
        part_name: selectedSKU?.particulars || partForm.part_name,
        quantity: partForm.quantity,
        required_in_days: partForm.required_in_days ? parseInt(partForm.required_in_days) : null,
      }]
    });
    setPartForm({ sku_id: '', part_name: '', quantity: 1, required_in_days: '' });
  };

  const removePart = (index) => {
    setLogForm({
      ...logForm,
      parts_required: logForm.parts_required.filter((_, i) => i !== index)
    });
  };

  const getSectionAssets = (section) => {
    const TRUCK_TYPES = ['truck', 'lmv', 'dumper'];
    const isTruck = (a) => TRUCK_TYPES.some(t => a.asset_type?.toLowerCase().includes(t));
    if (section === 'truck') return assets.filter(isTruck);
    // Machinery = everything that is not a truck/LMV/dumper
    return assets.filter(a => !isTruck(a));
  };

  const filteredLogs = logs.filter(l => {
    const matchesSection = l.section === activeSection;
    const matchesRepairStatus = repairStatusFilter === 'all' || l.repair_status === repairStatusFilter;
    
    // Apply additional filters
    let matchesDateRange = true;
    if (filters.dateRange?.from) {
      const logDate = new Date(l.reporting_time);
      const fromDate = new Date(filters.dateRange.from);
      fromDate.setHours(0, 0, 0, 0);
      const toDate = filters.dateRange.to ? new Date(filters.dateRange.to) : new Date(filters.dateRange.from);
      toDate.setHours(23, 59, 59, 999);
      matchesDateRange = logDate >= fromDate && logDate <= toDate;
    }
    
    const matchesVehicle = !filters.vehicle || l.door_no === filters.vehicle;
    const matchesMaintenanceType = !filters.maintenanceType || l.maintenance_type === filters.maintenanceType;
    const matchesStatus = !filters.status || l.status === filters.status;
    
    return matchesSection && matchesRepairStatus && matchesDateRange && matchesVehicle && matchesMaintenanceType && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Maintenance</h1>
          <p className="text-slate-600 mt-1">Track maintenance logs for trucks and machinery</p>
        </div>
        <div className="flex gap-2">
          {(user?.is_admin || user?.is_super_admin) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setIsImportOpen(true); setImportResult(null); }}
              data-testid="import-maintenance-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import Excel
            </Button>
          )}
          <Button 
            onClick={() => {
              setLogForm({ ...logForm, section: activeSection });
              setIsAddLogOpen(true);
            }} 
            className="bg-slate-900 hover:bg-slate-800 gap-2"
            data-testid="add-maintenance-log"
          >
            <LogIn className="h-4 w-4" />
            Add In
          </Button>
          <Button
            variant="outline"
            onClick={() => setIsAddOutOpen(true)}
            className="gap-2 border-green-600 text-green-700 hover:bg-green-50"
            data-testid="add-out-btn"
          >
            <LogOut className="h-4 w-4" />
            Add Out
          </Button>
        </div>
      </div>

      {/* Repair Status Summary Cards */}
      <PendingDeletionsPanel module="maintenance" requests={pendingDelRequests} onApprove={handleApproveDelete} onReject={handleRejectDelete} />
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(REPAIR_STATUS_CONFIG).filter(([key]) => key !== 'completed').map(([key, config]) => {
            const IconComponent = config.icon;
            const count = stats[key]?.count || 0;
            return (
              <Card 
                key={key} 
                className={`cursor-pointer transition-all hover:shadow-md ${
                  repairStatusFilter === key ? 'ring-2 ring-slate-900' : ''
                }`}
                onClick={() => setRepairStatusFilter(repairStatusFilter === key ? 'all' : key)}
                data-testid={`filter-${key}`}
              >
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">{config.label}</p>
                      <p className="text-xs text-slate-400">{config.description}</p>
                      <p className="text-2xl font-bold mt-1">{count}</p>
                    </div>
                    <div className={`p-3 rounded-full ${config.color}`}>
                      <IconComponent className="h-6 w-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Section Tabs */}
      <div className="flex items-center gap-2 mb-4">
        <Badge className={`text-sm py-1.5 px-3 ${activeSection === 'truck' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`}>
          {activeSection === 'truck' ? <Truck className="h-4 w-4 mr-2" /> : <Cog className="h-4 w-4 mr-2" />}
          {activeSection === 'truck' ? 'Truck Section' : 'Machinery Section'}
        </Badge>
      </div>

      {/* Filters */}
      <FilterBar 
        onReset={resetFilters} 
        activeFiltersCount={countActiveFilters(filters) + (repairStatusFilter !== 'all' ? 1 : 0)}
      >
        <DateRangeFilter
          label="Date Range"
          value={filters.dateRange}
          onChange={(val) => setFilters({ ...filters, dateRange: val })}
        />
        <SelectFilter
          label="Vehicle"
          value={filters.vehicle}
          onChange={(val) => setFilters({ ...filters, vehicle: val })}
          options={getSectionAssets(activeSection).map(a => ({ value: a.door_no, label: `${a.door_no} - ${a.make}` }))}
        />
        <SelectFilter
          label="Maintenance Type"
          value={filters.maintenanceType}
          onChange={(val) => setFilters({ ...filters, maintenanceType: val })}
          options={MAINTENANCE_TYPES.map(t => ({ value: t.id, label: t.label }))}
        />
        <SelectFilter
          label="Status"
          value={filters.status}
          onChange={(val) => setFilters({ ...filters, status: val })}
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'completed', label: 'Completed' }
          ]}
        />
      </FilterBar>

      {/* Maintenance Table */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-slate-500">{filteredLogs.length} records</span>
        <ExportButton
          rows={filteredLogs}
          columns={[
            { header: 'Date', key: 'date' },
            { header: 'Asset', key: 'door_no' },
            { header: 'Category', key: 'category' },
            { header: 'Description', key: 'description' },
            { header: 'Mechanic', key: 'mechanic_name' },
            { header: 'Status', key: 'status' },
            { header: 'Parts Used', key: r => (r.parts_used || []).map(p => `${p.part_name} x${p.quantity}`).join('; ') },
            { header: 'Created By', key: 'created_by' },
          ]}
          filename={`maintenance_logs_${new Date().toISOString().slice(0,10)}`}
          title="Maintenance Logs Report"
          subtitle={`Exported on ${new Date().toLocaleDateString('en-IN')}`}
        />
      </div>
      <MaintenanceTable 
        logs={filteredLogs} 
        onStatusChange={handleUpdateStatus}
        onDelete={(log) => handleDeleteLog(log)}
        onView={(log) => setViewingLog(log)}
        onEdit={(log) => openEditLog(log)}
        user={user}
      />

      {/* Add In (Maintenance Log) Modal */}
      <Dialog open={isAddLogOpen} onOpenChange={setIsAddLogOpen}>
        <DialogContent className="max-w-2xl flex flex-col max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              <span className="flex items-center gap-2">
                <LogIn className="h-5 w-5 text-slate-700" />
                Add In — {activeSection === 'truck' ? 'Truck Section' : 'Machinery Section'}
              </span>
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddLog} className="flex flex-col flex-1 min-h-0">
            {/* Scrollable body */}
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Door No. *</Label>
                <SearchableSelect
                  value={logForm.door_no}
                  onValueChange={(val) => setLogForm({ ...logForm, door_no: val })}
                  options={getSectionAssets(activeSection).map(a => ({ value: a.door_no, label: `${a.door_no} - ${a.make} ${a.model}` }))}
                  placeholder="Select asset"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Type of Maintenance *</Label>
                <SearchableSelect
                  value={logForm.maintenance_type}
                  onValueChange={(val) => setLogForm({ ...logForm, maintenance_type: val })}
                  options={MAINTENANCE_TYPES.map(t => ({ value: t.id, label: t.label }))}
                  placeholder="Select type"
                  className="mt-1.5"
                />
              </div>
            </div>

            <div>
              <Label>In Time — Vehicle Arrived at Garage *</Label>
              <Input
                type="datetime-local"
                value={logForm.reporting_time}
                onChange={(e) => setLogForm({ ...logForm, reporting_time: e.target.value })}
                required
                className="mt-1.5"
              />
              <p className="text-xs text-slate-500 mt-1">
                Enter the actual date/time the vehicle arrived — the system will auto-assign the breakdown bucket based on elapsed time.
              </p>
            </div>

            <div>
              <Label>Diagnosis *</Label>
              <Textarea
                value={logForm.diagnosis}
                onChange={(e) => setLogForm({ ...logForm, diagnosis: e.target.value })}
                required
                className="mt-1.5"
                rows={3}
                placeholder="Describe the issue and diagnosis..."
                data-testid="diagnosis-input"
              />
              {logForm.diagnosis && checkDiagnosisQuality(logForm.diagnosis) && (
                <div className="mt-1.5 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="diagnosis-warning">
                  <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                  <span>{checkDiagnosisQuality(logForm.diagnosis)} This entry will be flagged for admin review.</span>
                </div>
              )}
            </div>

            {/* Remarks */}
            <div>
              <Label>Remarks</Label>
              <Textarea
                value={logForm.remarks}
                onChange={(e) => setLogForm({ ...logForm, remarks: e.target.value })}
                className="mt-1.5"
                rows={2}
                placeholder="Any additional notes or observations..."
                data-testid="remarks-input"
              />
            </div>

            {/* Damage Photos */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Camera className="h-4 w-4" /> Damage Photos
                </Label>
                <span className="text-xs text-slate-400">{logForm.damage_photos.length}/5 photos</span>
              </div>
              {logForm.damage_photos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {logForm.damage_photos.map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={photo}
                        alt={`damage-${idx}`}
                        className="w-20 h-20 object-cover rounded-md border border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {logForm.damage_photos.length < 5 && (
                <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600 hover:text-slate-900 border border-dashed border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50 transition-colors w-fit" data-testid="photo-upload-label">
                  <Camera className="h-4 w-4" />
                  Upload Photos
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePhotoUpload}
                    data-testid="photo-upload-input"
                  />
                </label>
              )}
              <p className="text-xs text-slate-400">Photos are compressed automatically. Max 5 photos.</p>
            </div>

            {/* Parts Required Section */}
            <div className="border rounded-lg p-4 space-y-4">
              <Label className="text-base font-semibold">Parts Required</Label>
              <p className="text-sm text-slate-500">Parts added here will create tickets in inventory management</p>
              
              <div className="flex gap-2 items-end flex-wrap">
                <div className="flex-1 min-w-[160px]">
                  <Label className="text-sm">Select from Inventory</Label>
                  <SearchableSelect
                    value={partForm.sku_id}
                    onValueChange={(val) => setPartForm({ ...partForm, sku_id: val })}
                    options={inventory.map(s => ({ value: s.id, label: `${s.particulars} (${s.part_number})` }))}
                    placeholder="Select part"
                    className="mt-1"
                  />
                </div>
                <div className="w-20">
                  <Label className="text-sm">Qty</Label>
                  <Input
                    type="number"
                    min="1"
                    value={partForm.quantity}
                    onChange={(e) => setPartForm({ ...partForm, quantity: parseInt(e.target.value) || 1 })}
                    className="mt-1"
                    data-testid="part-qty-input"
                  />
                </div>
                <div className="w-32">
                  <Label className="text-sm">Reqd. in Days</Label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="e.g. 7"
                    value={partForm.required_in_days}
                    onChange={(e) => setPartForm({ ...partForm, required_in_days: e.target.value })}
                    className="mt-1"
                    data-testid="part-required-days-input"
                  />
                </div>
                <Button type="button" variant="outline" onClick={addPart} className="mt-5">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              <div>
                <Label className="text-sm">Or enter manually</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    placeholder="Part name"
                    value={partForm.part_name}
                    onChange={(e) => setPartForm({ ...partForm, part_name: e.target.value, sku_id: '' })}
                    className="flex-1"
                  />
                </div>
              </div>

              {logForm.parts_required.length > 0 && (
                <div className="mt-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Part Name</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Reqd. Days</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logForm.parts_required.map((part, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{part.part_name}</TableCell>
                          <TableCell className="text-right">{part.quantity}</TableCell>
                          <TableCell className="text-right">
                            {part.required_in_days ? `${part.required_in_days}d` : '—'}
                          </TableCell>
                          <TableCell>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => removePart(idx)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
            </div>{/* end scrollable body */}

            {/* Sticky footer — always visible */}
            <div className="flex justify-end gap-2 pt-4 mt-2 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => { setIsAddLogOpen(false); resetLogForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 gap-2">
                <LogIn className="h-4 w-4" /> Record In
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Out (Close Ticket) Modal */}
      <Dialog open={isAddOutOpen} onOpenChange={(open) => { if (!open) { setIsAddOutOpen(false); resetOutForm(); } }}>
        <DialogContent className="max-w-lg flex flex-col max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogOut className="h-5 w-5 text-green-600" />
              Add Out — Record Departure & Close Ticket
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddOut} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
              {/* Select open ticket */}
              <div>
                <Label>Select Open Ticket *</Label>
                <SearchableSelect
                  value={outForm.log_id}
                  onValueChange={(val) => setOutForm({ ...outForm, log_id: val })}
                  options={logs
                    .filter(l => l.status !== 'completed' && l.section === activeSection)
                    .map(l => ({
                      value: l.id,
                      label: `${l.door_no} — ${l.diagnosis?.slice(0, 40)}… (In: ${l.reporting_time ? new Date(l.reporting_time).toLocaleDateString('en-IN') : '?'})`,
                    }))
                  }
                  placeholder="Select ticket to close"
                  className="mt-1.5"
                  data-testid="out-log-select"
                />
              </div>

              {/* Show in-time and calculate man-hours preview */}
              {outForm.log_id && (() => {
                const selectedTicket = logs.find(l => l.id === outForm.log_id);
                const inTime = selectedTicket?.reporting_time;
                let previewHours = null;
                if (inTime && outForm.out_time) {
                  const diff = (new Date(outForm.out_time) - new Date(inTime)) / 3600000;
                  if (diff > 0) previewHours = diff.toFixed(2);
                }
                return (
                  <div className="bg-slate-50 rounded-lg px-4 py-3 text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Vehicle</span>
                      <span className="font-semibold">{selectedTicket?.door_no}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">In Time</span>
                      <span>{inTime ? new Date(inTime).toLocaleString('en-IN') : '—'}</span>
                    </div>
                    {previewHours && (
                      <div className="flex justify-between text-green-700 font-medium">
                        <span className="flex items-center gap-1"><Timer className="h-3.5 w-3.5" />Man-hours</span>
                        <span>{previewHours} hrs</span>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div>
                <Label>Out Time *</Label>
                <Input
                  type="datetime-local"
                  value={outForm.out_time}
                  onChange={(e) => setOutForm({ ...outForm, out_time: e.target.value })}
                  required
                  className="mt-1.5"
                  data-testid="out-time-input"
                />
              </div>

              <div>
                <Label>Close Remarks</Label>
                <Textarea
                  value={outForm.close_remarks}
                  onChange={(e) => setOutForm({ ...outForm, close_remarks: e.target.value })}
                  className="mt-1.5"
                  rows={3}
                  placeholder="Work done summary, parts replaced, observations..."
                  data-testid="close-remarks-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4 mt-2 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => { setIsAddOutOpen(false); resetOutForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800 gap-2">
                <LogOut className="h-4 w-4" /> Record Out & Close
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Request Dialog */}
      <DeleteRequestDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        module="maintenance"
        entryId={deleteTarget?.id}
        entryDisplay={deleteTarget?.display}
        onSuccess={() => setDeleteTarget(null)}
      />

      {/* ── Import Excel Modal ───────────────────────────────────────────── */}
      <Dialog open={isImportOpen} onOpenChange={(v) => { setIsImportOpen(v); if (!v) setImportResult(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Import Breakdown List from Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Template download */}
            <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3 border">
              <div>
                <p className="text-sm font-medium text-slate-800">Download Template</p>
                <p className="text-xs text-slate-500 mt-0.5">Get the correct column format before uploading</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                <Download className="h-4 w-4 mr-2" />
                Template
              </Button>
            </div>

            {/* Column guide */}
            <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 space-y-1">
              <p className="font-semibold text-blue-800 mb-1.5">Required & Optional Columns:</p>
              <p><span className="font-medium">door_no</span> <span className="text-red-500">*</span> — Vehicle/asset number (e.g. TK-001)</p>
              <p><span className="font-medium">diagnosis</span> <span className="text-red-500">*</span> — Issue description / fault details</p>
              <p><span className="font-medium">date</span> — Date of breakdown (DD-MM-YYYY or DD/MM/YYYY)</p>
              <p><span className="font-medium">maintenance_type</span> — Breakdown | Running with Problems | Running Repair | Scheduled | Preventive</p>
              <p><span className="font-medium">section</span> — truck | machinery (default: truck)</p>
              <p><span className="font-medium">status</span> — open | completed (default: open)</p>
            </div>

            {/* File upload */}
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleImport}
                disabled={importing}
                className="hidden"
                id="maintenance-import-file"
                data-testid="maintenance-import-file-input"
              />
              <label htmlFor="maintenance-import-file" className="cursor-pointer block">
                {importing
                  ? <Loader2 className="h-8 w-8 mx-auto text-slate-400 animate-spin" />
                  : <Upload className="h-8 w-8 mx-auto text-slate-400" />
                }
                <p className="mt-2 text-sm font-medium text-slate-700">
                  {importing ? 'Importing...' : 'Click to upload Excel / CSV'}
                </p>
                <p className="text-xs text-slate-400 mt-1">Supports .xlsx, .xls, .csv</p>
              </label>
            </div>

            {/* Import result */}
            {importResult && (
              <div className={`rounded-lg px-4 py-3 text-sm border ${
                importResult.imported > 0 ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-800'
              }`} data-testid="import-result">
                <p className="font-semibold mb-1">Import Complete</p>
                <p>Imported: <span className="font-bold">{importResult.imported}</span> records</p>
                {importResult.skipped > 0 && <p>Skipped (empty rows): {importResult.skipped}</p>}
                {importResult.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium text-amber-700">Errors ({importResult.errors.length}):</p>
                    <ul className="list-disc list-inside text-xs mt-1 space-y-0.5 max-h-24 overflow-y-auto">
                      {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsImportOpen(false); setImportResult(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Ticket Summary Modal ─────────────────────────────────────────── */}
      <Dialog open={!!viewingLog} onOpenChange={open => { if (!open) setViewingLog(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="text-xl font-bold text-slate-900">{viewingLog?.door_no}</span>
              {viewingLog && (
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
                  viewingLog.status === 'completed' ? 'bg-green-100 text-green-700' :
                  viewingLog.repair_status === 'long_term_breakdown' ? 'bg-red-100 text-red-700' :
                  viewingLog.repair_status === 'breakdown' ? 'bg-orange-100 text-orange-700' :
                  'bg-yellow-100 text-yellow-700'
                }`}>
                  {viewingLog.status === 'completed' ? 'Completed' :
                   viewingLog.repair_status === 'long_term_breakdown' ? 'Long Term Breakdown' :
                   viewingLog.repair_status === 'breakdown' ? 'Breakdown' : 'Running Repair'}
                </span>
              )}
              {viewingLog?.is_diagnosis_flagged && (
                <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold flex items-center gap-1">
                  <TriangleAlert className="h-3 w-3" /> Flagged
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {viewingLog && (
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
              {/* Key details grid */}
              <div className="grid grid-cols-2 gap-2.5 text-sm">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Maintenance Type</p>
                  <p className="font-medium capitalize">{viewingLog.maintenance_type?.replace(/_/g, ' ')}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Work Status</p>
                  <p className={`font-medium capitalize ${
                    viewingLog.status === 'completed' ? 'text-green-700' :
                    viewingLog.status === 'in_progress' ? 'text-blue-700' : 'text-orange-700'
                  }`}>{viewingLog.status?.replace(/_/g, ' ')}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><LogIn className="h-3 w-3 text-slate-400" /> In Time</p>
                  <p className="font-medium">
                    {viewingLog.reporting_time ? safeFormat(viewingLog.reporting_time, 'dd MMM yyyy, HH:mm') : '-'}
                  </p>
                  {viewingLog.reporting_time && viewingLog.status !== 'completed' && (
                    <p className="text-xs text-slate-400 mt-0.5">
                      {Math.floor((Date.now() - new Date(viewingLog.reporting_time)) / (1000 * 60 * 60))} hrs ago
                    </p>
                  )}
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><LogOut className="h-3 w-3 text-green-500" /> Out Time</p>
                  {viewingLog.out_time ? (
                    <p className="font-medium text-green-700">
                      {safeFormat(viewingLog.out_time, 'dd MMM yyyy, HH:mm')}
                    </p>
                  ) : (
                    <p className="text-slate-400 text-xs italic">Not yet recorded</p>
                  )}
                </div>
                {viewingLog.man_hours != null && (
                  <div className="bg-green-50 rounded-lg p-3 col-span-1">
                    <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Timer className="h-3 w-3 text-green-600" /> Man-Hours</p>
                    <p className="font-bold text-green-700 text-lg">{viewingLog.man_hours} hrs</p>
                  </div>
                )}
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Section</p>
                  <p className="font-medium capitalize">{viewingLog.section}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Logged By</p>
                  <p className="font-medium text-xs truncate">{viewingLog.created_by || '—'}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Date Logged</p>
                  <p className="font-medium">
                    {viewingLog.created_at ? safeFormat(viewingLog.created_at, 'dd MMM yyyy') : '—'}
                  </p>
                </div>
              </div>

              {/* Flagged reason */}
              {viewingLog.is_diagnosis_flagged && viewingLog.diagnosis_flag_reason && (
                <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1">
                    <TriangleAlert className="h-3.5 w-3.5" /> Flag Reason
                  </p>
                  <p className="text-xs text-amber-800">{viewingLog.diagnosis_flag_reason}</p>
                </div>
              )}

              {/* Diagnosis */}
              <div className="border rounded-lg p-3 bg-white">
                <p className="text-xs text-slate-500 mb-2 font-semibold uppercase tracking-wide">Diagnosis / Description</p>
                <p className="text-sm text-slate-800 leading-relaxed">{viewingLog.diagnosis || 'No diagnosis recorded'}</p>
              </div>

              {/* Remarks */}
              {viewingLog.remarks && (
                <div className="border rounded-lg p-3 bg-white">
                  <p className="text-xs text-slate-500 mb-2 font-semibold uppercase tracking-wide">Remarks (On In)</p>
                  <p className="text-sm text-slate-700 leading-relaxed">{viewingLog.remarks}</p>
                </div>
              )}

              {/* Close Remarks */}
              {viewingLog.close_remarks && (
                <div className="border border-green-200 rounded-lg p-3 bg-green-50">
                  <p className="text-xs text-green-600 mb-2 font-semibold uppercase tracking-wide flex items-center gap-1">
                    <LogOut className="h-3 w-3" /> Close Remarks
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">{viewingLog.close_remarks}</p>
                </div>
              )}

              {/* Damage Photos */}
              {viewingLog.damage_photos?.length > 0 && (
                <div className="border rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-2 font-semibold uppercase tracking-wide flex items-center gap-1">
                    <Camera className="h-3.5 w-3.5" /> Damage Photos ({viewingLog.damage_photos.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {viewingLog.damage_photos.map((photo, idx) => (
                      <a key={idx} href={photo} target="_blank" rel="noopener noreferrer">
                        <img
                          src={photo}
                          alt={`damage-${idx}`}
                          className="w-24 h-24 object-cover rounded-md border border-slate-200 hover:opacity-80 transition-opacity cursor-zoom-in"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Parts Required */}
              {viewingLog.parts_required?.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-3 py-2 border-b">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Parts Required ({viewingLog.parts_required.length})</p>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {viewingLog.parts_required.map((p, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-slate-700">{p.part_name || p.sku_id}</span>
                        <div className="flex items-center gap-2">
                          {p.required_in_days && (
                            <span className="text-orange-600 text-xs bg-orange-50 px-2 py-0.5 rounded border border-orange-200">
                              Reqd. in {p.required_in_days}d
                            </span>
                          )}
                          <span className="text-slate-500 text-xs bg-slate-100 px-2 py-0.5 rounded">Qty: {p.quantity}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="border border-dashed rounded-lg p-3 text-center text-xs text-slate-400">
                  No parts recorded for this ticket
                </div>
              )}

              {/* Footer actions */}
              <div className="flex justify-between items-center pt-1 pb-1">
                <p className="text-xs text-slate-400">
                  Last updated: {viewingLog.updated_at ? safeFormat(viewingLog.updated_at, 'dd MMM yyyy, HH:mm') : '—'}
                </p>
                <div className="flex gap-2">
                  {viewingLog.status !== 'completed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setViewingLog(null); setOutForm({ log_id: viewingLog.id, out_time: '', close_remarks: '' }); setIsAddOutOpen(true); }}
                      className="gap-2 border-green-500 text-green-700 hover:bg-green-50"
                      data-testid="view-modal-addout-btn"
                    >
                      <LogOut className="h-3.5 w-3.5" /> Add Out
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setViewingLog(null); openEditLog(viewingLog); }}
                    className="gap-2"
                    data-testid="view-modal-edit-btn"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit Ticket
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Edit Log Modal ───────────────────────────────────────────────── */}
      <Dialog open={isEditLogOpen} onOpenChange={open => { if (!open) { setIsEditLogOpen(false); setEditingLog(null); resetLogForm(); } }}>
        <DialogContent className="max-w-2xl flex flex-col max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Edit Maintenance Log — {editingLog?.door_no}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditLog} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Maintenance Type *</Label>
                <Select value={logForm.maintenance_type} onValueChange={v => setLogForm({ ...logForm, maintenance_type: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled Maintenance</SelectItem>
                    <SelectItem value="preventive">Preventive Maintenance</SelectItem>
                    <SelectItem value="running_repair">Running Repair</SelectItem>
                    <SelectItem value="running_with_problems">Running with Problems</SelectItem>
                    <SelectItem value="breakdown">Breakdown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Time of Reporting to Garage *</Label>
                <Input
                  type="datetime-local"
                  value={logForm.reporting_time}
                  onChange={e => setLogForm({ ...logForm, reporting_time: e.target.value })}
                  required
                  className="mt-1.5"
                />
              </div>
            </div>
            <div>
              <Label>Diagnosis / Description *</Label>
              <Textarea
                value={logForm.diagnosis}
                onChange={e => setLogForm({ ...logForm, diagnosis: e.target.value })}
                required
                className="mt-1.5"
                rows={3}
              />
            </div>
            <div>
              <Label>Remarks</Label>
              <Textarea
                value={logForm.remarks || ''}
                onChange={e => setLogForm({ ...logForm, remarks: e.target.value })}
                className="mt-1.5"
                rows={2}
                placeholder="Any additional notes..."
              />
            </div>
            </div>{/* end scrollable body */}
            <div className="flex justify-end gap-2 pt-4 mt-2 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => { setIsEditLogOpen(false); setEditingLog(null); resetLogForm(); }}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Maintenance Table Component
const MaintenanceTable = ({ logs, onStatusChange, onDelete, onView, onEdit, user }) => {
  if (logs.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-8 text-center">
        <Wrench className="h-12 w-12 text-slate-400 mx-auto mb-4" />
        <p className="text-slate-500">No maintenance logs for this section</p>
      </div>
    );
  }

  const getTypeLabel = (type) => {
    switch (type) {
      case 'scheduled': return 'Scheduled';
      case 'preventive': return 'Preventive';
      case 'running_repair': return 'Running Repair';
      case 'running_with_problems': return 'Running w/ Problems';
      case 'breakdown': return 'Breakdown';
      default: return type;
    }
  };

  const getRepairStatusBadge = (repairStatus, status) => {
    if (status === 'completed') {
      return (
        <Badge className="bg-green-100 text-green-800 border border-green-300">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Completed
        </Badge>
      );
    }
    
    const config = REPAIR_STATUS_CONFIG[repairStatus] || REPAIR_STATUS_CONFIG.running_repair;
    const IconComponent = config.icon;
    
    return (
      <Badge className={`${config.color} border`}>
        <IconComponent className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead>Door No.</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>In Time</TableHead>
            <TableHead>Out Time</TableHead>
            <TableHead>Diagnosis</TableHead>
            <TableHead>Parts</TableHead>
            <TableHead>Repair Status</TableHead>
            <TableHead>Work Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => (
            <TableRow
              key={log.id}
              data-testid={`log-row-${log.id}`}
              onClick={() => onView(log)}
              className="cursor-pointer hover:bg-blue-50/40 transition-colors group"
            >
              <TableCell>
                <span className="font-semibold text-slate-900">{log.door_no}</span>
              </TableCell>
              <TableCell>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  log.maintenance_type === 'running_repair' ? 'bg-red-100 text-red-700' :
                  log.maintenance_type === 'preventive' ? 'bg-blue-100 text-blue-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {getTypeLabel(log.maintenance_type)}
                </span>
              </TableCell>
              <TableCell className="text-sm">
                {log.reporting_time ? safeFormat(log.reporting_time, 'MMM d, yyyy HH:mm') : '-'}
              </TableCell>
              <TableCell className="text-sm">
                {log.out_time ? (
                  <div>
                    <span>{safeFormat(log.out_time, 'MMM d, yyyy HH:mm')}</span>
                    {log.man_hours != null && (
                      <span className="ml-1 text-xs text-green-600 font-medium">({log.man_hours}h)</span>
                    )}
                  </div>
                ) : (
                  <span className="text-slate-400">-</span>
                )}
              </TableCell>
              <TableCell className="max-w-xs text-sm">
                <div className="flex items-start gap-1.5">
                  {log.is_diagnosis_flagged && (
                    <span
                      title={`Flagged: ${log.diagnosis_flag_reason}`}
                      className="inline-flex shrink-0 items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-700"
                      data-testid={`diagnosis-flag-${log.id}`}
                    >
                      <TriangleAlert className="h-3 w-3" />
                      Flagged
                    </span>
                  )}
                  <span className="truncate text-slate-700 group-hover:text-blue-600 group-hover:underline underline-offset-2 transition-colors" title="Click row to view full details">
                    {log.diagnosis}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                {log.parts_required?.length > 0 ? (
                  <Badge variant="outline">{log.parts_required.length} parts</Badge>
                ) : '-'}
              </TableCell>
              <TableCell>
                {getRepairStatusBadge(log.repair_status, log.status)}
              </TableCell>
              <TableCell onClick={e => e.stopPropagation()}>
                <Select 
                  value={log.status} 
                  onValueChange={(val) => onStatusChange(log.id, val)}
                >
                  <SelectTrigger className="w-32 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(log)}
                    className="text-slate-600 hover:text-blue-700"
                    data-testid={`edit-log-${log.id}`}
                    title="Edit this log"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onDelete(log)}
                    className="text-red-600 hover:text-red-800"
                    data-testid={`delete-log-${log.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default MaintenancePage;
